import { useState } from 'react';
import type { CheckSummaryDto } from '@ops-monitor/shared';

interface HourRow {
  hour: string;
  all_count: number;
  synced_count: number;
  not_synced_count: number;
  waiting_count: number;
}

/**
 * Hourly solve_practice sync breakdown as a stacked bar chart.
 *
 * Segments PARTITION the total (synced + not_synced + waiting = all), so three
 * segments per bar. Palette is dataviz-validated (all six checks, light+dark):
 * blue #2a78d6 / amber #c47f10 / red #c23434, with 2px surface gaps between
 * segments and a labeled legend — the gaps + legend are the secondary
 * encoding, so the colors never carry identity alone. Per-mark hover tooltip
 * (the exact numbers also live in the table below the chart).
 */
export function HourlyChart({ check }: { check: CheckSummaryDto }) {
  const [tip, setTip] = useState<{ x: number; y: number; row: HourRow } | null>(null);
  const rows = (Array.isArray(check.value) ? (check.value as HourRow[]) : []).filter((r) => r && typeof r.all_count === 'number');
  if (rows.length === 0) return <div className="kpi__meta">нет данных за 24 часа</div>;

  const max = Math.max(...rows.map((r) => r.all_count), 1);
  const fmtHour = (iso: string): string => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:00`;
  };
  const fmtFull = (iso: string): string =>
    new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="hourly">
      <div className="hourly__legend" aria-hidden="false">
        <span>
          <i style={{ background: 'var(--chart-synced)' }} /> SYNCED
        </span>
        <span>
          <i style={{ background: 'var(--chart-not-synced)' }} /> NOT_SYNCED
        </span>
        <span>
          <i style={{ background: 'var(--chart-waiting)' }} /> WAITING
        </span>
      </div>
      <div className="hourly__bars" role="img" aria-label="Почасовой расклад статусов отправки за 24 часа">
        {rows.map((row) => (
          <div
            key={row.hour}
            className="hourly__col"
            onMouseMove={(event) => setTip({ x: event.clientX, y: event.clientY, row })}
            onMouseLeave={() => setTip(null)}
          >
            {(
              [
                ['synced', 'var(--chart-synced)', row.synced_count],
                ['not_synced', 'var(--chart-not-synced)', row.not_synced_count],
                ['waiting', 'var(--chart-waiting)', row.waiting_count],
              ] as const
            ).map(([key, color, count]) =>
              count > 0 ? (
                <div
                  key={key}
                  className="hourly__seg"
                  style={{
                    background: color,
                    height: `${Math.max((count / max) * 100, count > 0 ? 2 : 0)}%`,
                  }}
                />
              ) : null,
            )}
            {/* ensure a visible column even when everything is zero */}
            {row.all_count === 0 ? <div className="hourly__seg" style={{ background: 'var(--border)', height: '2px' }} /> : null}
          </div>
        ))}
      </div>
      <div className="hourly__xaxis">
        <span>{fmtHour(rows[0]!.hour)}</span>
        <span>24 часа (МСК)</span>
        <span>{fmtHour(rows.at(-1)!.hour)}</span>
      </div>
      {tip ? (
        <div className="hourly__tip" style={{ left: Math.min(tip.x + 14, window.innerWidth - 200), top: tip.y + 14 }}>
          <strong>{fmtFull(tip.row.hour)}</strong>
          <div>всего: {tip.row.all_count.toLocaleString('ru-RU')}</div>
          <div>synced: {tip.row.synced_count.toLocaleString('ru-RU')}</div>
          <div>not_synced: {tip.row.not_synced_count.toLocaleString('ru-RU')}</div>
          <div>waiting: {tip.row.waiting_count.toLocaleString('ru-RU')}</div>
        </div>
      ) : null}
    </div>
  );
}
